import { useEffect, useState } from 'react'
import { BookOpen, Loader2, Check, Mail, Lock, AlertCircle, Github, LogIn } from 'lucide-react'
import { useAuth } from '../lib/auth-context'

const RELIGIONS = [
  'Islam', 'Christianity', 'Judaism', 'Hinduism',
  'Buddhism', 'Atheism', 'Agnostic', 'Other',
]

const BELIEFS: Record<string, string[]> = {
  Islam: ['Sunni', 'Shia', 'Sufi', 'Quranist', 'Other'],
  Christianity: ['Catholic', 'Protestant', 'Orthodox', 'Evangelical', 'Non-denominational', 'Other'],
  Judaism: ['Orthodox', 'Conservative', 'Reform', 'Other'],
  Hinduism: ['Vaishnavism', 'Shaivism', 'Shaktism', 'Other'],
  Buddhism: ['Theravada', 'Mahayana', 'Vajrayana', 'Other'],
  Atheism: ['Secular', 'Antitheist', 'Other'],
  Agnostic: ['Spiritual', 'Skeptic', 'Other'],
  Other: ['Spiritual but not religious', 'Prefer not to say'],
}

const DEFAULT_BELIEFS = ['Other', 'Prefer not to say']

const VALUE_PROPS = [
  { icon: '✓', text: '14,200+ sourced theological answers' },
  { icon: '✓', text: '7 major religious traditions compared' },
  { icon: '✓', text: 'Verified scripture citations every time' },
]

interface LoginProps {
  onComplete: () => void
  onGuestStart: () => void
}

export function Login({ onComplete, onGuestStart }: LoginProps) {
  const { user, profile, saveProfile, loading, signInWithEmail, signUp, signInWithGoogle, signInWithGithub, resetPassword } = useAuth()
  const [step, setStep] = useState<'auth' | 'profile'>('auth')
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState('')
  const [authLoading, setAuthLoading] = useState(false)
  const [name, setName] = useState('')
  const [religion, setReligion] = useState('')
  const [belief, setBelief] = useState('')
  const [saving, setSaving] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  useEffect(() => {
    if (user) setStep('profile')
  }, [user])

  useEffect(() => {
    if (profile?.religion) {
      setReligion(profile.religion)
      setBelief(profile.belief || '')
      setName(profile.name || '')
    }
  }, [profile])

  async function handleEmailAuth(e: React.FormEvent) {
    e.preventDefault()
    setAuthError('')
    if (!email.trim() || !password.trim()) return
    setAuthLoading(true)
    const result = mode === 'signin'
      ? await signInWithEmail(email, password)
      : await signUp(email, password)
    setAuthLoading(false)
    if (result.error) {
      setAuthError(result.error)
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setResetLoading(true)
    const result = await resetPassword(email)
    setResetLoading(false)
    if (result.error) {
      setAuthError(result.error)
    } else {
      setResetSent(true)
    }
  }

  async function handleSaveProfile() {
    if (!name.trim() || !religion) return
    setSaving(true)
    await saveProfile({ name: name.trim(), religion, belief: belief || null })
    setSaving(false)
    onComplete()
  }

  async function handleGoogleSignIn() {
    setAuthLoading(true)
    await signInWithGoogle()
    setAuthLoading(false)
  }

  async function handleGithubSignIn() {
    setAuthLoading(true)
    await signInWithGithub()
    setAuthLoading(false)
  }

  if (step === 'auth') {
    if (showReset) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] px-4">
          <div className="w-full max-w-sm">
            <div className="mb-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--accent-subtle))]">
                <BookOpen className="h-7 w-7 text-[hsl(var(--accent))]" />
              </div>
              <h1 className="text-h3 text-[hsl(var(--fg))]">Reset Password</h1>
              <p className="text-small text-[hsl(var(--fg-muted))] mt-1">We'll send you a reset link</p>
            </div>

            <div className="card space-y-5">
              {resetSent ? (
                <div className="text-center py-4">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--success-bg))]">
                    <Check className="h-6 w-6 text-[hsl(var(--success))]" />
                  </div>
                  <p className="text-body text-[hsl(var(--fg))]">Check your email</p>
                  <p className="text-small text-[hsl(var(--fg-muted))] mt-2">If an account exists for {email}, you'll receive a password reset link shortly.</p>
                  <button
                    onClick={() => { setShowReset(false); setResetSent(false) }}
                    className="btn-ghost mt-4 text-small"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="label">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--fg-muted))]" />
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="input pl-10"
                      />
                    </div>
                  </div>

                  {authError && (
                    <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--error-bg))] p-3">
                      <AlertCircle className="h-4 w-4 text-[hsl(var(--error))] mt-0.5 shrink-0" />
                      <p className="text-small text-[hsl(var(--error))]">{authError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!email.trim() || resetLoading}
                    className="btn-cta w-full disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {resetLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send Reset Link
                  </button>

                  <button
                    type="button"
                    onClick={() => { setShowReset(false); setAuthError('') }}
                    className="btn-ghost w-full justify-center text-small"
                  >
                    Back to Sign In
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--accent-subtle))]">
              <BookOpen className="h-7 w-7 text-[hsl(var(--accent))]" />
            </div>
            <h1 className="text-h3 text-[hsl(var(--fg))]">Al-Munazir AI</h1>
            <p className="text-small text-[hsl(var(--fg-muted))] mt-1">
              {mode === 'signin' ? 'Welcome back — continue your journey' : 'Begin exploring the world\'s religions'}
            </p>
          </div>

          {/* Value props */}
          <div className="mb-6 space-y-2">
            {VALUE_PROPS.map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-small text-[hsl(var(--fg-muted))]">
                <span className="text-[hsl(var(--accent))] font-bold">{icon}</span>
                {text}
              </div>
            ))}
          </div>

          <div className="card space-y-5">
            <div className="flex rounded-lg border border-[hsl(var(--border))] p-0.5">
              <button
                onClick={() => { setMode('signin'); setAuthError('') }}
                className={`flex-1 rounded-md px-4 py-2 text-small font-medium transition-all ${
                  mode === 'signin' ? 'bg-[hsl(var(--accent))] text-white' : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => { setMode('signup'); setAuthError('') }}
                className={`flex-1 rounded-md px-4 py-2 text-small font-medium transition-all ${
                  mode === 'signup' ? 'bg-[hsl(var(--accent))] text-white' : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]'
                }`}
              >
                Create Account
              </button>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div>
                <label className="label">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--fg-muted))]" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="input pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--fg-muted))]" />
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="input pl-10"
                  />
                </div>
              </div>

              {mode === 'signin' && (
                <button
                  type="button"
                  onClick={() => { setShowReset(true); setAuthError('') }}
                  className="text-caption text-[hsl(var(--accent))] hover:underline -mt-2"
                >
                  Forgot password?
                </button>
              )}

              {authError && (
                <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--error-bg))] p-3">
                  <AlertCircle className="h-4 w-4 text-[hsl(var(--error))] mt-0.5 shrink-0" />
                  <p className="text-small text-[hsl(var(--error))]">{authError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={!email.trim() || !password.trim() || authLoading}
                className="btn-cta w-full disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {authLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[hsl(var(--border))]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[hsl(var(--surface))] px-2 text-small text-[hsl(var(--fg-muted))]">or continue with</span>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={handleGoogleSignIn}
                disabled={loading || authLoading}
                className="btn-secondary w-full justify-center disabled:opacity-50"
              >
                {loading || authLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                )}
                Google
              </button>

              <button
                onClick={handleGithubSignIn}
                disabled={loading || authLoading}
                className="btn-secondary w-full justify-center disabled:opacity-50"
              >
                {loading || authLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                )}
                GitHub
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[hsl(var(--border))]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[hsl(var(--surface))] px-2 text-small text-[hsl(var(--fg-muted))]">or</span>
              </div>
            </div>

            <button
              onClick={onGuestStart}
              className="btn-ghost w-full justify-center gap-2 text-small"
            >
              <LogIn className="h-4 w-4" />
              Continue as Guest — No Account Needed
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--accent-subtle))]">
            <BookOpen className="h-7 w-7 text-[hsl(var(--accent))]" />
          </div>
          <h1 className="text-h3 text-[hsl(var(--fg))]">Welcome</h1>
          <p className="text-small text-[hsl(var(--fg-muted))] mt-1">Tell us about yourself (optional)</p>
        </div>

        <div className="card space-y-5">
          <div>
            <label className="label">Your Name <span className="text-[hsl(var(--fg-muted))]">(optional)</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter your name"
              className="input"
            />
          </div>

          <div>
            <label className="label">Your Religion / Belief <span className="text-[hsl(var(--fg-muted))]">(optional)</span></label>
            <div className="grid grid-cols-2 gap-2">
              {RELIGIONS.map(r => (
                <button
                  key={r}
                  onClick={() => { setReligion(r); setBelief('') }}
                  className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-small transition-all ${
                    religion === r
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent-subtle))] text-[hsl(var(--accent))]'
                      : 'border-[hsl(var(--border))] text-[hsl(var(--fg))] hover:border-[hsl(var(--accent))/0.3]'
                  }`}
                >
                  {r}
                  {religion === r && <Check className="h-4 w-4 ml-1 shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {religion && (
            <div>
              <label className="label">Denomination / Sect <span className="text-[hsl(var(--fg-muted))]">(optional)</span></label>
              <div className="flex flex-wrap gap-2">
                {(BELIEFS[religion] || DEFAULT_BELIEFS).map(b => (
                  <button
                    key={b}
                    onClick={() => setBelief(b)}
                    className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-small transition-all ${
                      belief === b
                        ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent-subtle))] text-[hsl(var(--accent))]'
                        : 'border-[hsl(var(--border))] text-[hsl(var(--fg-muted))] hover:border-[hsl(var(--accent))/0.3]'
                    }`}
                  >
                    {belief === b && <Check className="h-3 w-3" />}
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="text-caption text-[hsl(var(--fg-muted))] text-center">
            This helps the AI tailor responses to your background. All fields optional — you can update later.
          </p>

          <div className="flex gap-3">
            <button
              onClick={onComplete}
              className="btn-ghost flex-1 justify-center"
            >
              Skip
            </button>
            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="btn-cta flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {profile ? 'Save & Continue' : 'Get Started'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
